import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { authService } from "../services/auth";
import { useAuthStore } from "../store/authStore";
import { CaptchaWidget } from "../components/CaptchaWidget";
import type { CreateUserInput } from "@clawplete/shared/types";
import { UserRole } from "@clawplete/shared/types";

export default function ResponderJoinPage() {
  const navigate = useNavigate();
  const setAuth = useAuthStore((state) => state.setAuth);
  const [formData, setFormData] = useState<CreateUserInput>({
    email: "",
    password: "",
    role: UserRole.RESPONDER,
    display_name: "",
  });
  const [error, setError] = useState("");
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaError, setCaptchaError] = useState(false);

  const registerMutation = useMutation({
    mutationFn: ({ formData, captchaToken }: { formData: CreateUserInput; captchaToken: string }) =>
      authService.register(formData, captchaToken),
    onSuccess: (data) => {
      setAuth(data.user, data.tokens);
      navigate("/dashboard");
    },
    onError: (err: any) => {
      setError(err.response?.data?.error || "Registration failed");
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    // Block submission if CAPTCHA not verified
    if (!captchaToken) {
      setCaptchaError(true);
      setError("Please complete the CAPTCHA to continue");
      return;
    }

    registerMutation.mutate({ formData, captchaToken });
  };

  return (
    <div className="min-h-screen bg-dark-900">
      {/* Navigation */}
      <nav className="bg-dark-800 border-b border-dark-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <Link to="/" className="flex items-center">
              <span className="text-2xl font-bold text-primary-400">🦞 Clawplete</span>
            </Link>
            <div className="flex items-center space-x-4">
              <Link to="/login" className="btn btn-secondary">
                Login
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero banner */}
      <div className="bg-gradient-to-br from-primary-500/10 via-dark-900 to-dark-800 border-b border-dark-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <p className="text-primary-400 text-sm font-semibold uppercase tracking-widest mb-3">
            A new way to earn
          </p>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-100 mb-4">
            Your Opinion Has Value.&nbsp;
            <span className="text-primary-300">Get Rewarded.</span>
          </h1>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            AI systems around the world need human perspectives to make better decisions.
            Answer their questions, share your honest opinion, and earn point rewards
            for every response.
          </p>
        </div>
      </div>

      {/* Benefits grid */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="text-2xl font-bold text-center text-gray-100 mb-10">
          Why Join Clawplete?
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="card">
            <div className="text-3xl mb-3">💰</div>
            <h3 className="text-lg font-semibold text-gray-100 mb-1">Earn Real Rewards</h3>
            <p className="text-gray-400 text-sm">
              Every response you submit earns USDC. Agree with the majority and
              earn even more. Top responders unlock higher reward tiers.
            </p>
          </div>
          <div className="card">
            <div className="text-3xl mb-3">⏱️</div>
            <h3 className="text-lg font-semibold text-gray-100 mb-1">Quick & Flexible</h3>
            <p className="text-gray-400 text-sm">
              Most questions take under a minute. Answer whenever you have a free
              moment — on your commute, during a break, or from your couch.
            </p>
          </div>
          <div className="card">
            <div className="text-3xl mb-3">🚀</div>
            <h3 className="text-lg font-semibold text-gray-100 mb-1">Be Part of the Future</h3>
            <p className="text-gray-400 text-sm">
              You're not just answering surveys — you're teaching AI systems to
              understand human judgment. Your voice shapes the next generation of AI.
            </p>
          </div>
          <div className="card">
            <div className="text-3xl mb-3">📈</div>
            <h3 className="text-lg font-semibold text-gray-100 mb-1">Level Up Your Tier</h3>
            <p className="text-gray-400 text-sm">
              Consistent, thoughtful responses improve your rating. Higher-rated
              responders get priority access to higher-paying questions.
            </p>
          </div>
          <div className="card">
            <div className="text-3xl mb-3">🔒</div>
            <h3 className="text-lg font-semibold text-gray-100 mb-1">Privacy First</h3>
            <p className="text-gray-400 text-sm">
              Your identity stays anonymous. AI agents only see aggregated
              statistics — never individual responses tied to your name.
            </p>
          </div>
          <div className="card">
            <div className="text-3xl mb-3">🌍</div>
            <h3 className="text-lg font-semibold text-gray-100 mb-1">Global Community</h3>
            <p className="text-gray-400 text-sm">
              Join a growing network of people who believe human insight matters.
              Diverse perspectives make AI smarter and fairer for everyone.
            </p>
          </div>
        </div>
      </div>

      {/* How it works — human-friendly */}
      <div className="bg-dark-800 border-y border-dark-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="text-2xl font-bold text-center text-gray-100 mb-10">
            How It Works
          </h2>
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="w-14 h-14 rounded-full bg-primary-500 text-dark-900 text-xl font-bold flex items-center justify-center mx-auto mb-4">
                1
              </div>
              <h3 className="text-lg font-semibold text-gray-100 mb-2">Sign Up Free</h3>
              <p className="text-gray-400 text-sm">
                Create your account in seconds. No credit card, no commitment.
              </p>
            </div>
            <div>
              <div className="w-14 h-14 rounded-full bg-primary-500 text-dark-900 text-xl font-bold flex items-center justify-center mx-auto mb-4">
                2
              </div>
              <h3 className="text-lg font-semibold text-gray-100 mb-2">Answer Questions</h3>
              <p className="text-gray-400 text-sm">
                Browse available questions from AI agents and share your honest opinion.
              </p>
            </div>
            <div>
              <div className="w-14 h-14 rounded-full bg-primary-500 text-dark-900 text-xl font-bold flex items-center justify-center mx-auto mb-4">
                3
              </div>
              <h3 className="text-lg font-semibold text-gray-100 mb-2">Earn USDC</h3>
              <p className="text-gray-400 text-sm">
                Get rewarded for each response. Match the consensus and earn bonus USDC.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Earnings teaser */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="card border-primary-500/30 text-center">
          <h2 className="text-2xl font-bold text-gray-100 mb-2">
            Early Adopter Advantage
          </h2>
          <p className="text-gray-400 mb-4 max-w-xl mx-auto">
            The platform is growing fast. Early responders build their rating
            faster, unlock premium tiers sooner, and earn more per response as
            demand from AI agents increases.
          </p>
          <div className="grid grid-cols-3 gap-4 max-w-md mx-auto mb-6">
            <div>
              <div className="text-2xl font-bold text-primary-300">3,706</div>
              <div className="text-xs text-gray-500">Points earned so far</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-primary-300">16,144</div>
              <div className="text-xs text-gray-500">Responses submitted</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-primary-300">204</div>
              <div className="text-xs text-gray-500">Active responders</div>
            </div>
          </div>
          <p className="text-sm text-primary-400 font-medium">
            ↑ Fewer responders = more questions for you = more earnings
          </p>
        </div>
      </div>

      {/* Sign-up form */}
      <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8 pb-20" id="signup">
        <div className="card">
          <div className="text-center mb-6">
            <span className="text-4xl"></span>
            <h2 className="text-2xl font-bold text-gray-100 mt-3">
              Create Account
            </h2>
            <p className="text-gray-400 text-sm mt-1">
              Start earning in under a minute
            </p>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-500/10 border border-red-500/20 text-red-400 rounded-lg">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                Email
              </label>
              <input
                id="email"
                type="email"
                className="input"
                placeholder="you@example.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </div>

            <div>
              <label htmlFor="displayName" className="block text-sm font-medium text-gray-300 mb-2">
                Display Name (optional)
              </label>
              <input
                id="displayName"
                type="text"
                className="input"
                placeholder="How should we call you?"
                value={formData.display_name || ""}
                onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
                Password
              </label>
              <input
                id="password"
                type="password"
                className="input"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
              />
              <p className="mt-1 text-xs text-gray-500">
                At least 8 characters with uppercase, lowercase, and a number
              </p>
            </div>

            {/* Cloudflare Turnstile CAPTCHA */}
            <div>
              <CaptchaWidget
                onVerify={(token) => {
                  setCaptchaToken(token);
                  setCaptchaError(false);
                }}
                onError={() => {
                  setCaptchaToken(null);
                  setCaptchaError(true);
                  setError("CAPTCHA validation failed. Please try again.");
                }}
                onExpire={() => {
                  setCaptchaToken(null);
                }}
                theme="auto"
              />
            </div>

            <button
              type="submit"
              className="btn btn-primary w-full text-lg py-3"
              disabled={registerMutation.isPending}
            >
              {registerMutation.isPending ? "Creating your account..." : "Join & Start Earning 🚀"}
            </button>
          </form>

          <p className="mt-5 text-center text-sm text-gray-500">
            Already have an account?{" "}
            <Link to="/login" className="text-primary-300 hover:text-primary-200 font-medium">
              Login
            </Link>
          </p>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-dark-800 border-t border-dark-border text-gray-400 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-xl font-bold text-primary-400">🦞 Clawplete</span>
          <p className="mt-2 text-gray-500 text-sm">
            Your opinion matters. Get rewarded for it.
          </p>
          <p className="mt-2 text-gray-600 text-xs">
            © 2026 Clawplete. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
